// these are hooks made for CTAWithIntegrations in the homepage, but should be able to use else where to.
// spike card with documentation: https://anywhereworks.atlassian.net/browse/SW-877
import { useRef, useState, useEffect, useCallback } from "react";

/**
 * @typedef {Object} CarouselHookReturn
 * @property {React.RefObject<HTMLUListElement>} ulRef - The reference object of the ul element which is the carousel container.
 * @property {React.RefObject<HTMLLIElement>[]} liRefs - An array of reference objects corresponding to each li element in the carousel.
 * @property {number} currentIndex - The current active index in the carousel. Reflects the currently displayed item.
 * @property {boolean} isHovered - Indicates whether the carousel is currently being hovered. Used to pause auto-scrolling when the carousel is being interacted with.
 * @property {function} setCurrentIndex - Setter function for currentIndex. Can be used to programmatically change the carousel's current item.
 * @property {function} setIsHovered - Setter function for isHovered. Can be used to programmatically set the hover state of the carousel.
 * @property {boolean} transitioning - Indicates whether a transition is currently happening in the carousel. Useful for controlling user interactions during transitions.
 * @property {Object} handlers - Collection of event handlers related to hover and touch actions.
 * @property {function} handlers.handleMouseEnter - Event handler for the 'mouseenter' event. Activates the hover state and sets the current index to the hovered item.
 * @property {function} handlers.handleMouseLeave - Event handler for the 'mouseleave' event. Deactivates the hover state.
 * @property {function} handlers.handleTouchStart - Event handler for the 'touchstart' event. Sets the current index to the touched item and activates the hover state after a short delay.
 * @property {function} handlers.handleTouchEnd - Event handler for the 'touchend' event. Deactivates the hover state and cancels the delayed hover activation if it hasn't fired yet.
 * @property {function} handlers.savePreviousIndex - Stores the current index before a transition starts. This can be used to return to the previous item after a transition.
 * @property {function} handlers.setPreviousIndex - Sets the current index to the previously stored index. This can be used to return to the previous item after a transition.
 */

/**
 * Custom hook to manage an auto-advancing infinite scroll carousel.
 *
 * @param {Object} options - Configuration options for the carousel.
 * @param {Array} options.arrayWithoutDups - The array of items to be displayed in the carousel. Each item corresponds to a li element.
 * @param {number} options.numberOfDupsInStart - The number of duplicate items at the start of the carousel. These duplicates are used to give the impression of infinite scrolling.
 * @param {number} options.autoPlayDuration - The interval (in milliseconds) at which the carousel should auto-scroll to the next item.
 * @param {number} options.transitionDuration - The duration (in milliseconds) of the scroll animation. Controls the speed of the transition between items.
 * @param {function} options.transitionFunction - The easing function to be used in the scroll animation. Determines the pace of the transition.
 * @param {boolean} options.enableHover - Specifies whether the carousel should respond to hover events.
 * @param {boolean} options.enableTouch - Specifies whether the carousel should respond to touch events.
 *
 * @returns {CarouselHookReturn} Various states, handlers, and refs for managing and interacting with the carousel.
 */
export const useCarousel = ({
  arrayWithoutDups,
  numberOfDupsInStart,
  autoPlayDuration,
  transitionDuration,
  transitionFunction,
  enableHover = true,
  enableTouch = true,
}) => {
  const ulRef = useRef();
  const liRefs = useRef([]);
  const prevIndexRef = useRef();
  const longPressTimeout = useRef();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [transitioning, setTransitioning] = useState(false);

  const handleMouseEnter = useCallback(
    (index) => {
      if (enableHover) {
        setIsHovered(true);
        setCurrentIndex(index - numberOfDupsInStart);
      }
    },
    [enableHover]
  );

  const handleMouseLeave = useCallback(() => {
    if (enableHover) {
      setIsHovered(false);
    }
  }, [enableHover]);

  const handleTouchStart = useCallback(
    (index) => {
      if (enableTouch) {
        setCurrentIndex(index - numberOfDupsInStart);
        longPressTimeout.current = setTimeout(() => {
          setIsHovered(true);
        }, 500);
      }
    },
    [enableTouch]
  );

  const handleTouchEnd = useCallback(() => {
    if (enableTouch) {
      clearTimeout(longPressTimeout.current);
      setIsHovered(false);
    }
  }, [enableTouch]);

  const savePreviousIndex = useCallback(() => {
    prevIndexRef.current = currentIndex;
  }, [currentIndex]);

  const setPreviousIndex = useCallback(() => {
    setCurrentIndex(prevIndexRef.current);
  }, []);

  const handlers = {
    handleMouseEnter,
    handleMouseLeave,
    handleTouchStart,
    handleTouchEnd,
    savePreviousIndex,
    setPreviousIndex,
  };

  const animateScroll = (element, to, duration) => {
    let start = element.scrollLeft,
      change = to - start,
      currentTime = 0,
      increment = 20;

    const animate = () => {
      currentTime += increment;
      const t = currentTime / duration; // t goes from 0 to 1
      element.scrollLeft = start + change * transitionFunction(t);
      if (currentTime < duration) {
        requestAnimationFrame(animate);
      }
    };

    animate();
  };

  useEffect(() => {
    liRefs.current.forEach((li, index) => {
      if (currentIndex == arrayWithoutDups.length || currentIndex == 0) {
        // remove active from current active card
        li.classList.remove("active");
        // make the starting card active - this will be the 3rd card (starting center card)
        liRefs.current[numberOfDupsInStart].classList.add("active");
        // make the duplicate of starting card active - this will be at the end
        liRefs.current[
          arrayWithoutDups.length + numberOfDupsInStart
        ].classList.add("active");
      } else if (index - numberOfDupsInStart === currentIndex) {
        li.classList.add("active");
      } else {
        li.classList.remove("active");
      }
    });
  }, [currentIndex]);

  useEffect(() => {
    let interval;
    if (!isHovered) {
      interval = setInterval(() => {
        let nextIndex = currentIndex + 1;

        if (nextIndex > arrayWithoutDups.length) {
          nextIndex = 0;
        }

        // Setting transitioning state to true to prevent user interaction while transition
        setTransitioning(true);

        animateScroll(
          ulRef.current,
          liRefs.current[nextIndex + numberOfDupsInStart].offsetLeft -
            ulRef.current.offsetWidth / 2 +
            liRefs.current[nextIndex + numberOfDupsInStart].offsetWidth / 2,
          transitionDuration
        );

        setCurrentIndex(nextIndex);
        // Check if we're at the end of the list
        if (nextIndex === arrayWithoutDups.length) {
          setTimeout(() => {
            // Set the scroll position back to the start, accounting for the duplicates. without animation to give infinite scroll effect
            ulRef.current.scrollTo({
              left:
                liRefs.current[numberOfDupsInStart].offsetLeft -
                ulRef.current.offsetWidth / 2 +
                liRefs.current[numberOfDupsInStart].offsetWidth / 2,
              behavior: "auto", // No animation
            });
            // Reset the current index back to 0
            setCurrentIndex(0);
            // Allowing user interaction after transition
            setTransitioning(false);
          }, transitionDuration);
        }
      }, autoPlayDuration);
    }

    return () => clearInterval(interval);
  }, [currentIndex, isHovered]);

  return {
    ulRef,
    liRefs,
    currentIndex,
    isHovered,
    setCurrentIndex,
    setIsHovered,
    transitioning,
    handlers,
  };
};
